import * as React from 'react';
import * as Moment from 'moment';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useWebPortalLegacyClient } from '../api/WebPortalTokenClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';

export enum DateTimeAccuracy {
    Millisecond,
    Second,
    Minute,
    Hour,
    Day,
    Month,
    Year,
    TimeOnly,
    TimeOnlyNoOffset
}

interface LocalizedDateTimeProps {
    time: string;
    accuracy: DateTimeAccuracy;
    isTooltip?: boolean;
    automationId?: string;
}

declare const rootUrl: string;

export function FormatTime(time: string, accuracy: DateTimeAccuracy, showOffset: boolean) {
    const { toolTipFormat, mainFormat } = GetFormat(accuracy);
    return Moment.parseZone(time).format(showOffset ? toolTipFormat : mainFormat);
}

function GetFormat(accuracy: DateTimeAccuracy) {
    let toolTipFormat = "DD-MMM-YYYY HH:mm:ss (Z)";
    let mainFormat = "DD-MMM-YYYY HH:mm:ss";

    switch (accuracy) {
        case DateTimeAccuracy.Year:
            mainFormat = "YYYY";
            toolTipFormat = "YYYY";
            break;
        case DateTimeAccuracy.Month:
            mainFormat = "MMM-YYYY";
            toolTipFormat = "MMM-YYYY";
            break;
        case DateTimeAccuracy.Day:
            mainFormat = "DD-MMM-YYYY";
            toolTipFormat = "DD-MMM-YYYY";
            break;
        case DateTimeAccuracy.Hour:
            mainFormat = "DD-MMM-YYYY HH";
            toolTipFormat = "DD-MMM-YYYY HH (Z)";
            break;
        case DateTimeAccuracy.Minute:
            mainFormat = "DD-MMM-YYYY HH:mm";
            toolTipFormat = "DD-MMM-YYYY HH:mm (Z)";
            break;
        case DateTimeAccuracy.Second:
            mainFormat = "DD-MMM-YYYY HH:mm:ss";
            toolTipFormat = "DD-MMM-YYYY HH:mm:ss (Z)";
            break;
        case DateTimeAccuracy.Millisecond:
            mainFormat = "DD-MMM-YYYY HH:mm:ss.SSS";
            toolTipFormat = "DD-MMM-YYYY HH:mm:ss.SSS (Z)";
            break;
        case DateTimeAccuracy.TimeOnly:
            mainFormat = "HH:mm:ss";
            toolTipFormat = "HH:mm:ss (Z)";
            break;
        case DateTimeAccuracy.TimeOnlyNoOffset:
            mainFormat = "HH:mm:ss";
            toolTipFormat = "HH:mm:ss";
            break;
    }

    return { toolTipFormat, mainFormat };
}

export const LocalizedDateTime = ({ time, accuracy, isTooltip = false, automationId = null }: LocalizedDateTimeProps) => {
    const [adjustedTime, setAdjustedTime] = React.useState<string>(null);
    const legacyClient = useWebPortalLegacyClient();

    const fetchData = (baseTime) => {
        legacyClient.getAdjustedUserTime(baseTime)
            .then(json => setAdjustedTime(json.time));
    }

    const displayLocalizedTime = () => {
        if (time) {
            fetchData(time);
        }
    }

    if (time) {
        const { toolTipFormat } = GetFormat(accuracy);

        const recordedDateTime = Moment.parseZone(time).format(toolTipFormat);

        let adjustedDateTimeString = "Loading...";
        let adjustedDateTime;
        if (adjustedTime) {
            adjustedDateTime = Moment.parseZone(adjustedTime);
            adjustedDateTimeString = Moment.parseZone(adjustedTime).format(toolTipFormat);
        }

        const tooltip = <Tooltip id="tooltip" style={{ zIndex: 2500 }}>
            Localized to you: {adjustedDateTimeString}
            <br />
            {adjustedDateTime ? adjustedDateTime.utc().fromNow() : null}
        </Tooltip>

        return (
            <OverlayTrigger placement="top" overlay={isTooltip ? tooltip : <></>}>
                <time data-automationid={automationId} dateTime={time} onMouseEnter={() => displayLocalizedTime()}>
                    {recordedDateTime}&nbsp;
                    <FontAwesomeIcon icon={faClock} className="text-muted" aria-hidden="true" />
                </time>
            </OverlayTrigger>
        );
    } else {
        return null;
    }

}
